import {memo, useMemo} from 'react';

import styled from 'styled-components';

import {media} from '~/shared/theme/utils';
import {LayoutWithHeader} from '~/shared/components/LayoutWithHeader';
import {flexColumn} from '~/shared/theme/FlexLayout';
import {MoneycardOtlRuleManagement} from '~/shared/store/models';
import {headingXS} from '~/shared/theme/typography';
import {getNonExpiredModifiedCreditCards} from '~/shared/utils/payments/payments';

import OtlRuleLimitations from './OtlRuleLimitations';
import OtlCreditCardList from './OtlCreditCardList';

const Root = styled.div`
  background-color: ${({theme}) => theme.colors.surface};
  ${flexColumn}
  padding: 32px 16px;
  width: 100%;
  height: 100%;
  ${media.minLargeMobile`
    padding: 32px;
    height: auto;
  `}
  ${media.minDesktop`
    width: 500px;
  `}
`;

const HeaderTitle = styled.h3`
  ${headingXS}
  text-align: center;
  color: ${({theme}) => theme.colors.contentDefault};
  width: 100%;
`;

type OtlRuleSettingsProps = {
  moneycardId: string;
  addCreditCard: () => void;
  shouldShowTermsOfUse: boolean;
  selectedCreditCard: number | null;
  setSelectedCreditCard: (selectedCreditCard: number | null) => void;
  onClose?: () => void;
  otlData: MoneycardOtlRuleManagement | null;
  onSuccess?: () => void;
  withoutHeader?: boolean;
  withoutCreditCardsList?: boolean;
  hideLimitations?: boolean;
  showBackArrow?: boolean;
  showCloseButton?: boolean;
  showUnlinkButton?: boolean;
  onOtlFailure?: () => void;
  onUnlinkSuccess?: (() => void) | (() => Promise<void>);
};

const OtlRuleSettings = ({
  moneycardId,
  addCreditCard,
  shouldShowTermsOfUse,
  selectedCreditCard,
  setSelectedCreditCard,
  onClose,
  otlData,
  onSuccess,
  withoutCreditCardsList,
  hideLimitations,
  withoutHeader,
  showBackArrow = true,
  showCloseButton = true,
  showUnlinkButton = false,
  onOtlFailure,
  onUnlinkSuccess,
}: OtlRuleSettingsProps) => {
  const {otlRule, userCreditcards} = otlData || {};

  const creditCardList = useMemo(
    () => getNonExpiredModifiedCreditCards(userCreditcards || []),
    [userCreditcards],
  );

  if (hideLimitations && withoutCreditCardsList) {
    return null;
  }

  const content = (
    <Root>
      {!withoutCreditCardsList && (
        <OtlCreditCardList {...{addCreditCard, creditCardList, selectedCreditCard, setSelectedCreditCard}} />
      )}
      {!hideLimitations && (
        <OtlRuleLimitations {...{
          moneycardId,
          otlRule,
          selectedCreditCard,
          onSuccess,
          showUnlinkButton,
          onOtlFailure,
          onUnlinkSuccess,
          shouldShowTermsOfUse,
        }}
        />
      )}
    </Root>
  );

  if (withoutHeader) {
    return content;
  }

  return (
    <LayoutWithHeader
      titleComponent={HeaderTitle}
      onClick={onClose}
      titleKey="link_credit_card_to_10bis"
      showBackArrow={showBackArrow}
      showCloseButton={showCloseButton}
    >
      {content}
    </LayoutWithHeader>
  );
};
export default memo(OtlRuleSettings);
